import React, { useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { useMessages } from '../../hooks/useMessages';
import FrictionlessMessageComposer from './FrictionlessMessageComposer';
import AvatarOrInitial from '../Family/AvatarOrInitial';
import { 
  MessageSquare, 
  User, 
  Clock, 
  Check, 
  CheckCheck, 
  AlertTriangle,
  Reply,
  Search,
  X,
  ArrowLeft,
  Calendar,
  Flag,
  Send
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

export default function FrictionlessFamilyMessages() {
  const navigate = useNavigate();
  const { family, currentUser } = useFamilyContext();
  const [activeTab, setActiveTab] = useState('send');
  const [searchQuery, setSearchQuery] = useState('');
  const [replyToMessage, setReplyToMessage] = useState(null);
  const composerRef = useRef(null);

  // Create stable filter object
  const messageFilters = useMemo(() => {
    const filters = {};
    
    if (activeTab === 'sent') {
      filters.senderId = currentUser?.id;
    }
    
    return filters;
  }, [activeTab, currentUser?.id]);
  
  const { 
    messages, 
    loading, 
    sendMessage,
    replyToMessage: replyToMessageFn,
    markAsRead
  } = useMessages(messageFilters);

  // Filter messages based on search
  const filteredMessages = messages.filter(message => {
    // Only show sent messages since we're removing inbox
    if (activeTab === 'sent' && message.sender_id !== currentUser?.id) return false;
    
    if (searchQuery.trim()) {
      const searchLower = searchQuery.toLowerCase();
      return (
        message.subject?.toLowerCase().includes(searchLower) ||
        message.content?.toLowerCase().includes(searchLower) ||
        message.sender_name?.toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  // Calculate tab counts
  const sentCount = messages.filter(msg => msg.sender_id === currentUser?.id).length;

  // Handle sending new message
  const handleSendMessage = async (messageData) => {
    try {
      // Determine message type based on user role if not specified
      let messageType = messageData.type;
      if (!messageType) {
        messageType = currentUser?.isParent ? 'from_parent' : 'to_parent';
      }
      
      await sendMessage({
        content: messageData.content,
        recipientIds: messageData.recipientIds,
        priority: messageData.priority || 'normal',
        type: messageType,
        dueDate: messageData.dueDate
      });
    } catch (error) {
      console.error('Failed to send:', error);
      throw error;
    }
  };

  // Handle replying to message
  const handleReply = async (messageData) => {
    if (!replyToMessage) return;
    
    try {
      await replyToMessageFn(replyToMessage.id, {
        content: messageData.content,
        recipientIds: messageData.recipientIds,
        priority: messageData.priority || 'normal',
        dueDate: messageData.dueDate
      });
      setReplyToMessage(null);
    } catch (error) {
      console.error('Failed to reply:', error);
      throw error;
    }
  };

  // Start reply
  const startReply = (message) => {
    setReplyToMessage(message);
    if (composerRef.current) {
      composerRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  // Cancel reply
  const cancelReply = () => {
    setReplyToMessage(null);
  };

  // Mark message as read when viewed
  const handleMessageView = async (message) => {
    if (message.recipients?.some(r => 
      r.recipient_id === currentUser?.id && r.status === 'unread'
    )) {
      await markAsRead(message.id);
    }
  };

  // Get family member info
  const getFamilyMember = (memberId) => {
    const allMembers = [...(family?.parents || []), ...(family?.kids || [])];
    return allMembers.find(m => m.id === memberId);
  };

  // Get message priority styling
  const getPriorityStyle = (priority) => {
    switch (priority) {
      case 'urgent':
        return 'border-l-red-500 bg-red-50';
      case 'high':
        return 'border-l-orange-500 bg-orange-50';
      default:
        return 'border-l-transparent';
    }
  };

  // Get message status for current user (only sent messages now)
  const getMessageStatus = (message) => {
    // For sent messages, show overall status
    const allRead = message.recipients?.every(r => r.status !== 'unread');
    const anyAcknowledged = message.recipients?.some(r => r.status === 'acknowledged');
    
    if (anyAcknowledged) return { icon: CheckCheck, color: 'text-indigo-800', label: 'Acknowledged' };
    if (allRead) return { icon: Check, color: 'text-magenta-600', label: 'Read' };
    return { icon: MessageSquare, color: 'text-stone-400', label: 'Sent' };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-magenta-600 mx-auto mb-4"></div>
          <p className="text-stone-600">Loading reminders...</p>         
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Mobile-Optimized Header */}
      <div className="bg-white border-b border-stone-200 sticky top-0 z-50">
        <div className="px-4 py-3">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate(-1)}
              className="p-2 text-stone-600 hover:bg-stone-100 rounded-lg transition-colors"
              style={{ minWidth: '44px', minHeight: '44px' }}
              aria-label="Go back"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            
            <div className="flex items-center gap-3">
              {/* Color bar */}
              <div className="w-2 h-6 bg-magenta-500 rounded-full"></div>
              <div>
                <h1 className="text-xl font-bold text-slate-900 tracking-tight">Family Reminders</h1>
              </div>
            </div>
          </div>
        </div>
        
        {/* Mobile Tab Navigation */}
        <div className="px-0">
          <div className="flex">
            <button
              onClick={() => setActiveTab('send')}
              className={`flex-1 px-4 py-4 text-sm font-medium transition-all duration-150 border-b-2 ${
                activeTab === 'send'
                  ? 'border-magenta-500 text-slate-900 bg-magenta-50'
                  : 'border-transparent text-stone-600 hover:text-slate-900 hover:bg-stone-50'
              }`}
              style={{ minHeight: '48px' }}
            >
              <div className="flex items-center justify-center gap-2">
                <Send className="w-4 h-4" />
                <span>Send a Reminder</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('sent')}
              className={`flex-1 px-4 py-4 text-sm font-medium transition-all duration-150 border-b-2 ${
                activeTab === 'sent'
                  ? 'border-magenta-500 text-slate-900 bg-magenta-50'
                  : 'border-transparent text-stone-600 hover:text-slate-900 hover:bg-stone-50'
              }`}
              style={{ minHeight: '48px' }}
            >
              <div className="flex items-center justify-center gap-2">
                <MessageSquare className="w-4 h-4" />
                <span>Sent Reminders</span>
                {sentCount > 0 && activeTab !== 'sent' && (
                  <span className="bg-stone-400 text-white text-xs px-2 py-0.5 rounded-full min-w-[18px] text-center">
                    {sentCount}
                  </span>
                )}
              </div>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6 space-y-4 pb-20">
        {/* Send Tab Content */}
        {activeTab === 'send' && (
          <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
            {!replyToMessage ? (
              <div ref={composerRef}>
                <FrictionlessMessageComposer
                  onSend={handleSendMessage}
                  autoFocus={true}
                />
              </div>
            ) : (
              /* Reply Composer */
              <div ref={composerRef}>
                <div className="bg-magenta-50 border-b border-magenta-200 p-4">
                  <div className="flex items-center gap-2 text-magenta-700 text-sm mb-2">
                    <Reply className="w-4 h-4" />
                    <span>Replying to reminder from {replyToMessage.sender_name}</span>
                    <button
                      onClick={cancelReply}
                      className="ml-auto text-magenta-600 hover:text-magenta-800 p-1"
                      style={{ minWidth: '32px', minHeight: '32px' }}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <p className="text-magenta-600 text-sm line-clamp-2">
                    {replyToMessage.content}
                  </p>
                </div>
                <FrictionlessMessageComposer
                  onSend={handleReply}
                  onCancel={cancelReply}
                  placeholder="Type your reply..."
                  autoFocus={true}
                  initialRecipients={[getFamilyMember(replyToMessage.sender_id)].filter(Boolean)}
                  showRecipientSelector={false}
                />
              </div>
            )}
          </div>
        )}

        {/* Sent Tab Content */}
        {activeTab === 'sent' && (
          <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
            {/* Search Header for Sent Tab */}
            <div className="p-4 bg-stone-50 border-b border-stone-200">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-stone-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search sent reminders..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-10 py-3 border-2 border-stone-200 rounded-lg focus:outline-none focus:border-magenta-500 text-base transition-all duration-150"
                  style={{ fontSize: '16px' }}
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-stone-400 hover:text-stone-600 p-1"
                    style={{ minWidth: '32px', minHeight: '32px' }}
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Messages List */}
            <div className="divide-y divide-stone-100">
              {filteredMessages.length === 0 ? (
                <div className="text-center py-16">
                  <div className="text-stone-300 mb-4">
                    <MessageSquare className="w-12 h-12 mx-auto" />
                  </div>
                  <h3 className="text-lg font-semibold text-stone-600 mb-2">No sent reminders yet</h3>
                  <p className="text-stone-500 max-w-xs mx-auto">Reminders you send to family members will appear here</p>
                </div>
              ) : (
                filteredMessages.map((message) => {
                  const status = getMessageStatus(message);
                  const StatusIcon = status.icon;
                  const sender = getFamilyMember(message.sender_id);
                  
                  return (
                    <div
                      key={message.id}
                      className={`p-4 hover:bg-stone-50 transition-colors cursor-pointer border-l-4 ${getPriorityStyle(message.priority)}`}
                      onClick={() => handleMessageView(message)}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                          {/* Sender Avatar */}
                          <AvatarOrInitial
                            avatar={sender?.avatar}
                            name={sender?.name || message.sender_name || 'U'}
                            size="lg"
                            className="flex-shrink-0"
                          />
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold text-slate-900 text-sm">
                                To: {message.recipients?.map(r => getFamilyMember(r.recipient_id)?.name || 'Unknown').join(', ')}
                              </h3>
                              <span className="text-xs text-stone-500 flex-shrink-0">
                                {formatDistanceToNow(new Date(message.created_at), { addSuffix: true })}
                              </span>
                            </div>
                            
                            {/* Priority & Due Date Indicators */}
                            <div className="flex items-center gap-2 mb-2">
                              {message.priority && message.priority !== 'normal' && (
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                                  message.priority === 'urgent' 
                                    ? 'bg-red-100 text-red-700'
                                    : 'bg-orange-100 text-orange-700'
                                }`}>
                                  <Flag className="w-3 h-3" />
                                  {message.priority}
                                </span>
                              )}
                                                             {message.due_date && (
                                 <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-magenta-100 text-magenta-700">
                                   <Calendar className="w-3 h-3" />
                                   {new Date(message.due_date).toLocaleDateString()}
                                 </span>
                               )}
                            </div>
                            
                            <p className="text-stone-700 text-sm line-clamp-3 mb-3">
                              {message.content}
                            </p>
                            
                            {/* Recipients Status (for sent messages) */}
                            {message.recipients && (
                              <div className="flex items-center gap-2">
                                <div className="flex gap-1">
                                  {message.recipients.map(recipient => {
                                    const member = getFamilyMember(recipient.recipient_id);
                                    return member ? (
                                      <span
                                        key={recipient.recipient_id}
                                        className="inline-flex items-center gap-1 bg-stone-100 text-stone-700 px-2 py-0.5 rounded-full text-xs"
                                      >
                                        {member.name.charAt(0)}
                                        {recipient.status === 'acknowledged' && (
                                          <CheckCheck className="w-3 h-3 text-indigo-600" />
                                        )}
                                        {recipient.status === 'read' && recipient.status !== 'acknowledged' && (
                                          <Check className="w-3 h-3 text-magenta-600" />
                                        )}
                                      </span>
                                    ) : null;
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {/* Status Icon */}
                          <StatusIcon className={`w-4 h-4 ${status.color}`} title={status.label} />
                          
                          {/* Reply Button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              startReply(message);
                              setActiveTab('send');
                            }}
                            className="p-2 text-stone-400 hover:text-magenta-600 transition-colors rounded-lg hover:bg-stone-100"
                            title="Reply"
                            style={{ minWidth: '36px', minHeight: '36px' }}
                          >
                            <Reply className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}